import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Chip,
  Paper,
  Grid,
  FormHelperText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Tooltip,
} from '@mui/material';
import { Add as AddIcon, CloudUpload as CloudUploadIcon, DashboardCustomize as DashboardCustomizeIcon } from '@mui/icons-material';
import { useFormContext } from 'react-hook-form';
import { apiService } from '../../../services/apiService';
import ProjectOwnerSelect from '../../ProjectOwnerSelect';
import { useAuth } from '../../../contexts/AuthContext';
import UseTemplateDialog, { idOf } from './UseTemplateDialog';
import { requiredStorageIds, withDefaultSelectedStorage } from '../../../utils/storageDefaults';

// Server populates project.owner_id as either a User doc ({_id, first_name,
// last_name, email}) or an Organization doc ({_id, name, short_name}) — or
// leaves it as a raw id if neither resolved. Reduce to a display string.
const ownerDisplay = (project) => {
  const o = project?.owner_id;
  if (!o || typeof o !== 'object') return '—';
  const full = `${o.first_name || ''} ${o.last_name || ''}`.trim();
  return full || o.name || o.short_name || o.email || '—';
};

// Capitalize first letter of the project type (research/education/etc).
const formatType = (t) => (typeof t === 'string' && t.length > 0
  ? t.charAt(0).toUpperCase() + t.slice(1)
  : '');

// Tooltip body for the selected project. Hovering the chosen row surfaces the
// description / deadline / observation count without consuming dialog space.
const renderProjectTooltip = (project) => (
  <Box sx={{ p: 0.5, maxWidth: 360 }}>
    <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
      {project.code} · {project.name}
    </Typography>
    {project.description && (
      <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
        {project.description}
      </Typography>
    )}
    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mt: 0.5 }}>
      <Typography variant="caption">
        Type: {formatType(project.type) || '—'}
      </Typography>
      <Typography variant="caption">
        Observations: {project.observation_count ?? project.stats?.total_observations ?? 0}
      </Typography>
      {project.deadline && (
        <Typography variant="caption">
          Deadline: {new Date(project.deadline).toLocaleDateString('en-US')}
        </Typography>
      )}
    </Box>
  </Box>
);

// Shared row renderer for the project dropdown items AND the Select's collapsed
// "selected value" display. Mirrors the helper in QuickProjectStep.js so the
// two observation forms render the project picker identically.
const renderProjectRow = (project) => (
  <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: 1.5 }}>
    <Typography
      variant="body2"
      sx={{ fontFamily: 'monospace', fontWeight: 600, width: 140, flexShrink: 0 }}
    >
      {project.code}
    </Typography>
    <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap>
      {project.name}
    </Typography>
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ width: 140, flexShrink: 0, textAlign: 'left' }}
      noWrap
    >
      {ownerDisplay(project)}
    </Typography>
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ width: 90, flexShrink: 0, textAlign: 'right' }}
      noWrap
    >
      {formatType(project.type)}
    </Typography>
  </Box>
);

const ProjectStep = () => {
  const { register, setValue, getValues, watch, formState: { errors } } = useFormContext();
  const { user, updateUserPreferences } = useAuth();
  const [projects, setProjects] = useState([]);
  const [observatories, setObservatories] = useState([]);
  const [owners, setOwners] = useState([]);
  const [ownersLoading, setOwnersLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [useTemplateOpen, setUseTemplateOpen] = useState(false);
  // Dedicated error for the Create Project dialog so it renders INSIDE that dialog
  // (the top-most one), not behind it in the parent step where it would be hidden.
  const [createError, setCreateError] = useState('');
  const [creating, setCreating] = useState(false);
  const [newProject, setNewProject] = useState({
    name: '',
    description: '',
    code: '',
    type: 'research',
    // Unset until chosen (#1177) — ProjectOwnerSelect fills it in when there is exactly one
    // eligible owner and asks when there is more than one.
    owner_type: null,
    owner_id: null
  });

  const [storageConfigs, setStorageConfigs] = useState([]);
  // Seeded from the form so a selection (including an unchecked default) survives stepping
  // back to this step, which remounts it.
  const [selectedStorageIds, setSelectedStorageIds] = useState(() => getValues('externalStorageDestinations') || []);

  const selectedProjectId = watch('projectId');
  const selectedObservatoryId = watch('assignedTo');

  // Storage the server adds to every new observation (#1474): the selected project's own
  // configs and the user's personal configs. Force-checked and locked so the form shows where
  // the data will go. Default-selected org storage (#1454) stays uncheckable.
  const mandatoryStorageIds = requiredStorageIds(storageConfigs, selectedProjectId);

  // Project-scoped configs belong to a specific project — only show the SELECTED
  // project's. Personal and organization configs always show.
  const visibleStorageConfigs = storageConfigs.filter(c =>
    c._scope !== 'project' || (selectedProjectId && String(c._scopeId) === String(selectedProjectId))
  );

  const userNotifs = user?.preferences?.notifications || {};
  const hasNotifications = userNotifs.email_enabled || userNotifs.pushover_enabled;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadProjects();
    loadObservatories();
    loadOrganizations();
    loadStorageConfigs();
    // Initialize notification defaults from user preferences.
    // Default to actionable failure states only (#805): auto-selecting all 7
    // lifecycle states stamped 'complete' on every observation, which drove the
    // spammy "Processing Complete" emails. Users can still opt into the noisier
    // states (assigned/in_progress/suspended/complete/cancelled) in the form.
    if (userNotifs.email_enabled) setValue('notifyEmail', true);
    if (userNotifs.pushover_enabled) setValue('notifyPushover', true);
    if (userNotifs.email_enabled || userNotifs.pushover_enabled) {
      setValue('notifyStates', ['failed', 'aborted', 'cancelled']);
    }
  }, []);

  // Save last selected observatory preference
  useEffect(() => {
    if (selectedObservatoryId && observatories.length > 0) {
      updateUserPreferences({ last_observatory_id: selectedObservatoryId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedObservatoryId]);

  const loadProjects = async () => {
    try {
      setLoading(true);
      setError('');
      // getAllProjects walks every page instead of just the default page of 20.
      // The server sorts by updated_at desc, so without this an active
      // project that hasn't been edited recently falls off the first page
      // and silently disappears from the picker (e.g. "Asteroids" at #22).
      const projectList = await apiService.getAllProjects({ status: 'active' });

      if (Array.isArray(projectList)) {
        // Filter to show only projects user can submit observations to,
        // then sort alphabetically so ordering doesn't depend on edit-recency.
        const editableProjects = projectList
          .filter(p => p.can_use || p.can_edit)
          .sort((a, b) => a.name.localeCompare(b.name));
        setProjects(editableProjects);

        // Check for prefilled project name from Target Library
        const prefillProjectName = sessionStorage.getItem('prefillProjectName');
        if (prefillProjectName) {
          sessionStorage.removeItem('prefillProjectName');
          // Find matching project by name (case-insensitive)
          const matchingProject = editableProjects.find(
            p => p.name.toLowerCase() === prefillProjectName.toLowerCase()
          );
          if (matchingProject) {
            setValue('projectId', matchingProject._id);
            console.log(`Auto-selected project "${matchingProject.name}" from Target Library folder`);
          }
        } else if (editableProjects.length === 1) {
          // If only one project and no prefill, select it automatically
          setValue('projectId', editableProjects[0]._id);
        }
      }
    } catch (err) {
      console.error('Failed to load projects:', err);
      setError('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const loadOrganizations = async () => {
    try {
      // Eligible project owners (#1177): Personal + every org the user belongs to (child orgs
      // included), each flagged with the subscription backing it. Same source as the other two
      // creation paths, so none of them can record an owner the others would have asked about.
      const response = await apiService.getEligibleProjectOwners();
      setOwners(Array.isArray(response?.data?.owners) ? response.data.owners : []);
    } catch (err) {
      console.error('Failed to load eligible project owners:', err);
      // Not critical - the picker hides itself and the server defaults to a personal project.
    } finally {
      setOwnersLoading(false);
    }
  };

  const loadObservatories = async () => {
    try {
      const response = await apiService.getObservatories();

      if (response.data && response.data.observatories) {
        // Show all observatories - users should be able to create observations
        // for any observatory (e.g., planning ahead for maintenance periods)
        setObservatories(response.data.observatories);

        // If only one observatory, select it automatically
        if (response.data.observatories.length === 1) {
          setValue('assignedTo', response.data.observatories[0]._id);
        } else if (response.data.observatories.length > 1) {
          // Pre-select from saved preference: last used takes priority, then profile default
          const lastObsId = user?.preferences?.last_observatory_id;
          const defaultObsId = user?.preferences?.default_observatory_id;
          const savedObsId = lastObsId || defaultObsId;
          if (savedObsId && response.data.observatories.find(o => o._id === savedObsId)) {
            setValue('assignedTo', savedObsId);
          }
        }
      }
    } catch (err) {
      console.error('Failed to load observatories:', err);
      // Don't set error here as it's not critical - the backend will default if needed
    }
  };

  const loadStorageConfigs = async () => {
    try {
      const response = await apiService.getAvailableStorageConfigs();
      if (response?.success) {
        // Build a flat list with scope metadata for grouped display
        const allConfigs = [];

        // Personal configs
        const personal = response.data?.personal || [];
        for (const config of personal) {
          allConfigs.push({ ...config, _scope: 'personal', _scopeLabel: 'Personal' });
        }

        // Organization configs
        const orgGroups = response.data?.organization || {};
        for (const [orgId, group] of Object.entries(orgGroups)) {
          for (const config of (group.configs || [])) {
            allConfigs.push({ ...config, _scope: 'organization', _scopeLabel: group.name, _scopeId: orgId });
          }
        }

        // Project configs
        const projGroups = response.data?.project || {};
        for (const [projId, group] of Object.entries(projGroups)) {
          for (const config of (group.configs || [])) {
            allConfigs.push({ ...config, _scope: 'project', _scopeLabel: group.name, _scopeId: projId });
          }
        }

        setStorageConfigs(allConfigs);
      }
    } catch (err) {
      // Non-critical: user may not have any configs
      console.debug('No external storage configs available');
    }
  };

  // Sync selected storage IDs to form
  useEffect(() => {
    setValue('externalStorageDestinations', selectedStorageIds);
  }, [selectedStorageIds, setValue]);

  // Pre-check organization storage its admin marked "selected by default" (#1454) — once per
  // form, so a member who unchecks it isn't overridden when the step remounts.
  useEffect(() => {
    if (storageConfigs.length === 0 || getValues('externalStorageDefaultsApplied')) return;
    setValue('externalStorageDefaultsApplied', true);
    setSelectedStorageIds(prev => withDefaultSelectedStorage(prev, storageConfigs));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageConfigs]);

  // Force the required storage to always be selected.
  useEffect(() => {
    if (mandatoryStorageIds.length === 0) return;
    setSelectedStorageIds(prev => {
      const missing = mandatoryStorageIds.filter(id => !prev.includes(id));
      return missing.length === 0 ? prev : [...prev, ...missing];
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProjectId, storageConfigs]);

  const handleStorageToggle = (configId) => {
    // Required storage is added by the server anyway — ignore attempts to uncheck it.
    if (mandatoryStorageIds.includes(configId)) return;
    setSelectedStorageIds(prev =>
      prev.includes(configId)
        ? prev.filter(id => id !== configId)
        : [...prev, configId]
    );
  };

  const handleCreateProject = async () => {
    try {
      setCreating(true);
      setCreateError('');

      // Clean up payload - don't send null owner_id
      const projectData = { ...newProject };
      if (!projectData.owner_id) {
        delete projectData.owner_id;
        delete projectData.owner_type;
      }

      const response = await apiService.createProject(projectData);
      
      if (response.data) {
        // Add new project to list
        setProjects([...projects, response.data]);
        // Select the new project
        setValue('projectId', response.data._id);
        // Close dialog
        setCreateDialogOpen(false);
        // Reset form
        setNewProject({
          name: '',
          description: '',
          code: '',
          type: 'research',
          owner_type: null,
          owner_id: null
        });
      }
    } catch (err) {
      console.error('Failed to create project:', err);
      setCreateError(err.response?.data?.error?.message || 'Failed to create project');
    } finally {
      setCreating(false);
    }
  };

  // Apply a saved ObservingTemplate to the form: observatory, project, and the
  // template's target list + top-level settings. Mirrors the field shapes the
  // observation form and UnifiedTargetStep expect (camelCase, string coords).
  const handleApplyTemplate = (template) => {
    const obsId = idOf(template.observatory_id);
    const projId = idOf(template.project_id);
    if (obsId) setValue('assignedTo', obsId);
    if (projId) setValue('projectId', projId);

    const formTargets = (template.targets || []).map((t) => ({
      name: t.name || '',
      naif_id: t.naif_id || '',   // #1409: carry spacecraft NAIF id from template into the form
      ra: t.ra != null ? String(t.ra) : '',
      dec: t.dec != null ? String(t.dec) : '',
      priority: t.priority ?? template.priority ?? 5,
      sequenceRepeatCount: t.sequence_repeat_count || 1,
      exposureConfigs: (t.exposure_configs || []).map((ec) => ({
        filter: ec.filter,
        exposureTime: ec.exposure_time,
        exposureCount: ec.exposure_count,
        binning: ec.binning != null ? String(ec.binning) : '1',
        gain: ec.gain ?? null,
        offset: ec.offset ?? null,
      })),
    }));
    if (formTargets.length) setValue('targets', formTargets);

    if (template.observation_type) setValue('observationType', template.observation_type);
    if (template.priority != null) setValue('priority', template.priority);
    if (template.global_repeat_count != null) setValue('globalRepeatCount', template.global_repeat_count);
    if (template.name && !watch('observationName')) setValue('observationName', template.name);
  };

  const selectedProject = projects.find(p => p._id === selectedProjectId);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="h6" sx={{ mb: 0 }}>
          Observation Details
        </Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<DashboardCustomizeIcon />}
          onClick={() => setUseTemplateOpen(true)}
        >
          Use Template
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Observation Name (Optional)"
            placeholder="e.g., M31 Luminance Series, NGC 2237 Ha Mapping"
            {...register('observationName', {
              minLength: {
                value: 3,
                message: 'Observation name must be at least 3 characters if provided'
              }
            })}
            // Float the label when a value is set programmatically (e.g. Use
            // Template); an uncontrolled field won't shrink on its own, so the
            // label would overlap the value. undefined = MUI default.
            InputLabelProps={{ shrink: Boolean(watch('observationName')) || undefined }}
            error={!!errors.observationName}
            helperText={errors.observationName?.message || 'Optional: A descriptive name for this observation set (will use target name if not provided)'}
          />
        </Grid>
        
        <Grid item xs={12}>
          <FormControl fullWidth error={!!errors.projectId}>
            <InputLabel>Project</InputLabel>
            <Select
              label="Project"
              value={selectedProjectId || ''}
              {...register('projectId', { required: 'Please select a project' })}
              renderValue={(val) => {
                const p = projects.find((x) => x._id === val);
                if (!p) return '';
                return (
                  <Tooltip title={renderProjectTooltip(p)} placement="bottom-start" arrow>
                    <Box sx={{ width: '100%' }}>{renderProjectRow(p)}</Box>
                  </Tooltip>
                );
              }}
            >
              {projects
                .slice()
                .sort((a, b) => String(a.code || '').localeCompare(String(b.code || ''), undefined, { sensitivity: 'base' }))
                .map((project) => (
                  <MenuItem key={project._id} value={project._id}>
                    {renderProjectRow(project)}
                  </MenuItem>
                ))}
            </Select>
            <FormHelperText error={!!errors.projectId}>
              {errors.projectId?.message || 'All observations require Project association for tracking purposes.'}
            </FormHelperText>
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => { setCreateError(''); setCreateDialogOpen(true); }}
            fullWidth
          >
            Create New Project
          </Button>
        </Grid>

        <Grid item xs={12}>
          <FormControl fullWidth error={!!errors.assignedTo}>
            <InputLabel>Observatory *</InputLabel>
            <Select
              label="Observatory *"
              value={selectedObservatoryId || ''}
              {...register('assignedTo', { required: 'Please select an observatory' })}
            >
              {observatories.map((observatory) => (
                <MenuItem key={observatory._id} value={observatory._id}>
                  <Box sx={{ width: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="body1">{observatory.name}</Typography>
                      {observatory.code && (
                        <Chip label={observatory.code} size="small" variant="outlined" />
                      )}
                      <Chip
                        label={observatory.status}
                        size="small"
                        color={observatory.status === 'online' ? 'success' : 'default'}
                      />
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {observatory.location?.city}, {observatory.location?.country}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
            {errors.assignedTo ? (
              <FormHelperText>{errors.assignedTo.message}</FormHelperText>
            ) : (
              <FormHelperText>
                Select the observatory that will execute this observation
              </FormHelperText>
            )}
          </FormControl>
        </Grid>
      </Grid>

      {/* Notification Preferences — fieldset/legend gives the same notched-
          label-on-border look as the OutlinedInput-based Select fields above,
          with a hover deepening matching MUI's default outlined input. */}
      {hasNotifications && (
        <Box
          component="fieldset"
          sx={{
            mt: 3,
            mx: 0,
            mb: 0,
            px: 1.5,
            pt: 1,
            pb: 1.5,
            minWidth: 0,
            border: '1px solid',
            borderColor: 'rgba(255,255,255,0.23)',
            borderRadius: 1,
            transition: 'border-color 0.15s',
            '&:hover': { borderColor: 'rgba(255,255,255,0.87)' },
          }}
        >
          <Box
            component="legend"
            sx={{
              px: 0.75,
              width: 'auto',
              fontSize: '0.75rem',
              color: 'text.secondary',
            }}
          >
            Notifications
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <FormGroup row>
              {userNotifs.email_enabled && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={watch('notifyEmail') || false}
                      onChange={(e) => setValue('notifyEmail', e.target.checked)}
                    />
                  }
                  label="Email"
                />
              )}
              {userNotifs.pushover_enabled && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={watch('notifyPushover') || false}
                      onChange={(e) => setValue('notifyPushover', e.target.checked)}
                    />
                  }
                  label="Pushover"
                />
              )}
            </FormGroup>
            <Button
              size="small"
              onClick={() => {
                setValue('notifyEmail', false);
                setValue('notifyPushover', false);
                setValue('notifyStates', []);
              }}
            >
              Clear All
            </Button>
          </Box>
          {(watch('notifyEmail') || watch('notifyPushover')) && (
            <>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap' }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem', mr: 2.5, flexShrink: 0 }}>
                  Notify me on:
                </Typography>
                {[
                  { value: 'assigned', label: 'Assigned' },
                  { value: 'in_progress', label: 'In Progress' },
                  { value: 'suspended', label: 'Suspended' },
                  { value: 'complete', label: 'NINA Completed' },
                  { value: 'failed', label: 'Failed' },
                  { value: 'aborted', label: 'Aborted' },
                  { value: 'cancelled', label: 'Cancelled' },
                ].map((state) => {
                  const currentStates = watch('notifyStates') || [];
                  return (
                    <FormControlLabel
                      key={state.value}
                      control={
                        <Checkbox
                          size="small"
                          checked={currentStates.includes(state.value)}
                          onChange={(e) => {
                            const updated = e.target.checked
                              ? [...currentStates, state.value]
                              : currentStates.filter(s => s !== state.value);
                            setValue('notifyStates', updated);
                          }}
                          sx={{ p: 0 }}
                        />
                      }
                      label={<Typography variant="caption" sx={{ fontSize: '0.65rem', ml: 0.25 }}>{state.label}</Typography>}
                      sx={{ mr: 2.5 }}
                    />
                  );
                })}
              </Box>
            </>
          )}
        </Box>
      )}
      {hasNotifications && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mt: 0.5, ml: 1.5 }}
        >
          Select the notification type desired for Observation state transitions
        </Typography>
      )}

      {/* External Storage Destinations (Issue #413) */}
      {visibleStorageConfigs.length > 0 && (
        <Paper elevation={1} sx={{ p: 3, mt: 3 }}>
          <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CloudUploadIcon color="primary" />
            External Storage Destinations (optional)
          </Typography>
          <Divider sx={{ my: 2 }} />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Select destinations to automatically copy FITS files after processing:
          </Typography>
          {/* Group configs by scope */}
          {(() => {
            const groups = {};
            for (const config of visibleStorageConfigs) {
              const key = config._scope === 'personal' ? 'Personal' : `${config._scope === 'organization' ? 'Org' : 'Project'}: ${config._scopeLabel}`;
              if (!groups[key]) groups[key] = { scope: config._scope, configs: [] };
              groups[key].configs.push(config);
            }
            return Object.entries(groups).map(([groupName, group]) => (
              <Box key={groupName} sx={{ mb: 1.5 }}>
                {Object.keys(groups).length > 1 && (
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.5 }}>
                    {groupName}
                  </Typography>
                )}
                {group.configs.map(config => {
                  const isMandatory = mandatoryStorageIds.includes(config._id);
                  return (
                  <FormControlLabel
                    key={config._id}
                    control={
                      <Checkbox
                        checked={isMandatory || selectedStorageIds.includes(config._id)}
                        disabled={isMandatory}
                        onChange={() => handleStorageToggle(config._id)}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2">
                          {config.display_name}
                          <Chip
                            label={config.provider === 'dropbox' ? 'Dropbox' : config.provider === 'gdrive' ? 'Google Drive' : config.provider === 'aws-s3' ? 'AWS S3' : config.provider === 'sftp' ? 'SFTP/SCP' : 'GCS'}
                            size="small"
                            variant="outlined"
                            sx={{ ml: 1 }}
                          />
                          {config._scope !== 'personal' && (
                            <Chip
                              label={config._scope === 'organization' ? 'Org' : 'Project'}
                              size="small"
                              color={config._scope === 'organization' ? 'info' : 'secondary'}
                              sx={{ ml: 0.5 }}
                            />
                          )}
                          {isMandatory && (
                            <Chip
                              label="Required"
                              size="small"
                              color="primary"
                              sx={{ ml: 0.5 }}
                            />
                          )}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {(() => {
                            const root = config.destination_path || '';
                            const template = config.custom_path || '';
                            if (!template) return root;
                            const resolved = template
                              .replace(/\$USER/g, user?.username || user?.first_name || 'user')
                              .replace(/\$PROJECT/g, selectedProject?.code || 'project')
                              .replace(/\$TARGET/g, '<target>');
                            return root ? `${root}/${resolved}` : resolved;
                          })()}
                          {config.delete_from_minio && ' (will delete from server after transfer)'}
                        </Typography>
                      </Box>
                    }
                    sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.5 }}
                  />
                  );
                })}
              </Box>
            ));
          })()}
        </Paper>
      )}

      {/* Use Template Dialog */}
      <UseTemplateDialog
        open={useTemplateOpen}
        onClose={() => setUseTemplateOpen(false)}
        onApply={handleApplyTemplate}
        observatories={observatories}
        projects={projects}
      />

      {/* Create Project Dialog */}
      <Dialog open={createDialogOpen} onClose={() => { setCreateDialogOpen(false); setCreateError(''); }} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Project</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {createError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setCreateError('')}>
                {createError}
              </Alert>
            )}
            <TextField
              fullWidth
              label="Project Name"
              value={newProject.name}
              onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
              sx={{ mb: 2 }}
              required
            />
            
            <TextField
              fullWidth
              label="Project Code"
              value={newProject.code}
              onChange={(e) => setNewProject({ ...newProject, code: e.target.value.toUpperCase() })}
              helperText="Unique identifier (e.g., ASTRO101-2025)"
              sx={{ mb: 2 }}
              required
            />
            
            <TextField
              fullWidth
              label="Description"
              value={newProject.description}
              onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
              multiline
              rows={3}
              sx={{ mb: 2 }}
              required
            />
            
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Project Type</InputLabel>
              <Select
                value={newProject.type}
                onChange={(e) => setNewProject({ ...newProject, type: e.target.value })}
                label="Project Type"
              >
                <MenuItem value="research">Research</MenuItem>
                <MenuItem value="education">Education</MenuItem>
                <MenuItem value="outreach">Outreach</MenuItem>
                <MenuItem value="personal">Personal</MenuItem>
                <MenuItem value="testing">Testing</MenuItem>
              </Select>
            </FormControl>

            <ProjectOwnerSelect
              owners={owners}
              loading={ownersLoading}
              value={{ owner_type: newProject.owner_type, owner_id: newProject.owner_id }}
              onChange={({ owner_type, owner_id }) => setNewProject({ ...newProject, owner_type, owner_id })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleCreateProject} 
            variant="contained"
            disabled={creating || !newProject.name || !newProject.code || !newProject.description
              // An owner must be settled first (#1177) — auto-filled when there is only one
              // eligible owner, picked otherwise. If owners failed to load the picker hides
              // itself, so don't deadlock the dialog on it.
              || (owners.length > 0 && !newProject.owner_type)}
          >
            {creating ? <CircularProgress size={24} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProjectStep;